import React from "react";
import { STATUS_OPS, opsForType, BLANK_FILTER, isRangeOp } from "./columnFilter";

// The inline filter row that sits under a report's header: one operator picker and one or two
// value boxes per column. Every report renders the same controls from this, so a change here --
// a new operator, say -- reaches all of them at once.
//
// cols is the table's LEAF columns in order, so the row lines up with the header and body:
//   { key, type?, style?, className? }
// type picks the operator set: 'text' for names, 'status' for an Active/Inactive picker, and
// anything else (the default) for figures.
// filters is the screen's { [key]: { op, v1, v2 } } map and onChange(key, patch) merges into it,
// which is what patchFilter in ./columnFilter does.
//
// Columns the screen does not want filtered (a checkbox or an action column) still need a cell to
// keep the row aligned -- give them { key, filterable: false } and they render empty.
function ColumnFilterRow({ cols, filters, onChange, thClassName = "px-1 py-1 border border-black bg-gray-50 align-top" }) {
    return (
        <tr>
            {cols.map((c) => {
                if (c.filterable === false) {
                    return <th key={c.key} className={thClassName} style={c.style} />;
                }
                const f = filters[c.key] || BLANK_FILTER;

                // A status column is one picker, not an operator plus a box. The op is pinned to
                // 'status' on every change so it can never fall back to 'contains', under which
                // Active would also match Inactive.
                if (c.type === 'status') {
                    return (
                        <th key={c.key} className={thClassName} style={c.style}>
                            <div className="flex flex-col gap-0.5 normal-case font-normal">
                                <select
                                    value={f.op === 'status' ? (f.v1 ?? '') : ''}
                                    onChange={(e) => onChange(c.key, { op: 'status', v1: e.target.value })}
                                    className="w-full min-w-0 text-[11px] border border-gray-300 rounded px-0.5 py-0.5 bg-white text-gray-700"
                                >
                                    {STATUS_OPS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
                                </select>
                            </div>
                        </th>
                    );
                }

                const ops = opsForType(c.type);
                return (
                    <th key={c.key} className={thClassName} style={c.style}>
                        <div className="flex flex-col gap-0.5 normal-case font-normal">
                            {/* w-full min-w-0 lets the picker shrink with its column. Left to
                                itself a select is as wide as its widest option -- "not between" --
                                which set a floor under every filtered column's width. */}
                            <select
                                value={f.op}
                                onChange={(e) => onChange(c.key, { op: e.target.value })}
                                className="w-full min-w-0 text-[11px] border border-gray-300 rounded px-0.5 py-0.5 bg-white text-gray-700"
                            >
                                {ops.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
                            </select>
                            <input
                                type="text"
                                value={f.v1 ?? ''}
                                onChange={(e) => onChange(c.key, { v1: e.target.value })}
                                placeholder={isRangeOp(f.op) ? 'min' : ''}
                                className="w-full min-w-0 text-[11px] border border-gray-300 rounded px-1 py-0.5"
                            />
                            {isRangeOp(f.op) && (
                                <input
                                    type="text"
                                    value={f.v2 ?? ''}
                                    onChange={(e) => onChange(c.key, { v2: e.target.value })}
                                    placeholder="max"
                                    className="w-full min-w-0 text-[11px] border border-gray-300 rounded px-1 py-0.5"
                                />
                            )}
                        </div>
                    </th>
                );
            })}
        </tr>
    );
}

export default ColumnFilterRow;
